import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { fetchCatalogProducts, fetchCatalogCategories } from "@/lib/api";
import type { CatalogProduct, CatalogCategory } from "@/types/api";
import { formatCents, cn } from "@/lib/utils";
import { SEOHead } from "@/components/shared/SEOHead";
import { getCatalogProductImageUrl, prefetchImages } from "@/lib/imagePrefetch";

export const Route = createFileRoute("/business/catalog")({
  component: BusinessCatalogPage,
});

function BusinessCatalogPage() {
  const [products, setProducts] = useState<CatalogProduct[]>([]);
  const [categories, setCategories] = useState<CatalogCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([fetchCatalogProducts(), fetchCatalogCategories()])
      .then(([productsRes, categoriesRes]) => {
        const activeProducts = productsRes.products.filter((p) => p.is_active);
        setProducts(activeProducts);
        setCategories(categoriesRes.categories);
        prefetchImages([
          ...activeProducts.map(getCatalogProductImageUrl),
          ...categoriesRes.categories.map((category) => category.thumbnail_image_url),
        ], 18);
        setLoading(false);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : "Failed to load catalog");
        setLoading(false);
      });
  }, []);

  const filteredProducts = useMemo(() => selectedCategory
    ? products.filter((p) => p.category === selectedCategory)
    : products, [products, selectedCategory]);

  useEffect(() => {
    if (!loading) {
      prefetchImages(filteredProducts.map(getCatalogProductImageUrl), 12);
    }
  }, [filteredProducts, loading]);

  return (
    <div className="page-enter bp-utility-page bp-business-page bp-catalog-page">
      <SEOHead
        title="Bulk Product Catalog"
        description="Every product you can put your art on, ready for bulk orders. Tees, hoodies, mugs, tote bags, stickers, and more."
        path="/business/catalog"
      />

      {/* Header */}
      <section className="bg-qds-background border-b border-qds-border" aria-labelledby="catalog-heading">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-10 md:py-14">
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm text-qds-text-tertiary mb-4">
            <Link to="/business" className="hover:text-qds-text-secondary transition-colors min-h-[44px] inline-flex items-center">
              Business
            </Link>
            <span aria-hidden="true">/</span>
            <span className="text-qds-text-primary" aria-current="page">Catalog</span>
          </nav>
          <h1 id="catalog-heading" className="text-3xl md:text-4xl font-light tracking-tight text-qds-text-primary mb-2">
            The Full Lineup
          </h1>
          <p className="text-qds-text-secondary text-sm">
            Everything you can put your design on, ready for bulk orders
          </p>
        </div>
      </section>

      {/* Filters + Grid */}
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-8 md:py-12 bp-catalog-body">
        {/* Category filters */}
        {categories.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-8" role="group" aria-label="Filter by category">
            <button
              onClick={() => setSelectedCategory(null)}
              className={cn(
                "px-4 py-2.5 rounded-lg text-sm font-medium transition-colors min-h-[44px]",
                !selectedCategory
                  ? "bg-qds-primary text-qds-text-inverse"
                  : "bg-qds-surface text-qds-text-secondary border border-qds-border hover:border-qds-border-strong"
              )}
              aria-pressed={!selectedCategory}
            >
              All Products
              {!selectedCategory && (
                <span className="sr-only"> (active filter)</span>
              )}
            </button>
            {categories.map((cat) => (
              <button
                key={cat.value}
                onClick={() => setSelectedCategory(cat.value)}
                className={cn(
                  "px-4 py-2.5 rounded-lg text-sm font-medium transition-colors min-h-[44px]",
                  selectedCategory === cat.value
                    ? "bg-qds-primary text-qds-text-inverse"
                    : "bg-qds-surface text-qds-text-secondary border border-qds-border hover:border-qds-border-strong"
                )}
                aria-pressed={selectedCategory === cat.value}
              >
                {cat.display_name || cat.value}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" aria-label="Loading catalog">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-64 skeleton rounded-xl" role="status" aria-label="Loading" />
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-20" role="alert">
            <div className="w-14 h-14 bg-qds-error-tint border border-qds-error/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-qds-error" aria-hidden="true">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <p className="text-qds-text-primary font-medium mb-1">Couldn't load the catalog</p>
            <p className="text-qds-text-secondary text-sm mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="text-sm font-medium text-qds-text-primary bg-qds-surface px-5 py-2.5 rounded-lg border border-qds-border hover:bg-qds-surface-hover transition-colors min-h-[44px]"
            >
              Try again
            </button>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-qds-text-primary font-medium mb-1">Nothing in this category</p>
            <p className="text-qds-text-secondary text-sm">
              {selectedCategory
                ? "Try a different filter or show all products."
                : "The catalog is being refreshed. Check back shortly."}
            </p>
            {selectedCategory && (
              <button
                onClick={() => setSelectedCategory(null)}
                className="mt-4 text-sm font-medium text-qds-text-primary underline min-h-[44px]"
              >
                Show all products
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 bp-catalog-grid">
              {filteredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>

            {/* Results count for screen readers */}
            <div className="sr-only" aria-live="polite">
              Showing {filteredProducts.length} of {products.length} products
            </div>
          </>
        )}
      </div>

      {/* CTA */}
      <section className="bg-qds-surface border-t border-qds-border bp-catalog-cta" aria-labelledby="catalog-cta-heading">
        <div className="max-w-3xl mx-auto px-5 md:px-8 py-12 text-center">
          <h2 id="catalog-cta-heading" className="text-xl font-medium text-qds-text-primary mb-2">
            Found what you need?
          </h2>
          <p className="text-sm text-qds-text-secondary mb-5">
            Tell us what products you want and we'll have pricing back to you in 24 hours.
          </p>
          <Link
            to="/business/quote"
            className="inline-flex items-center gap-2 bg-qds-primary text-qds-text-inverse px-6 py-3 rounded-xl font-medium text-sm hover:bg-qds-primary-pressed transition-colors min-h-[44px]"
          >
            Get a quote
          </Link>
        </div>
      </section>
    </div>
  );
}

function ProductCard({ product }: { product: CatalogProduct }) {
  const enabledVariants = product.variants?.filter((v) => v.is_enabled) ?? [];
  const minPrice = enabledVariants.reduce(
    (min, v) => (v.price_cents !== null && v.price_cents < min ? v.price_cents : min),
    Infinity
  );
  const colorOptions = new Set(
    enabledVariants.map((v) => v.options?.["Color"] || v.options?.["color"]).filter(Boolean)
  );
  const sizeOptions = new Set(
    enabledVariants.map((v) => v.options?.["Size"] || v.options?.["size"]).filter(Boolean)
  );

  const mockupUrl = getCatalogProductImageUrl(product);

  return (
    <article className="bg-qds-surface-elevated border border-qds-border rounded-xl overflow-hidden hover:border-qds-border-strong hover:shadow-sm transition-all bp-product-card">
      {/* Image — fixed aspect ratio prevents CLS */}
      <div className="aspect-[4/3] bg-qds-surface flex items-center justify-center bp-product-image">
        {mockupUrl ? (
          <img
            src={mockupUrl}
            alt={product.title}
            className="w-full h-full object-cover"
            loading="lazy"
            decoding="async"
            width={400}
            height={300}
          />
        ) : (
          <span className="text-qds-text-tertiary text-sm">{product.title}</span>
        )}
      </div>

      {/* Details */}
      <div className="p-5">
        <h3 className="font-medium text-qds-text-primary mb-1">{product.title}</h3>
        {product.brand && (
          <p className="text-xs text-qds-text-tertiary mb-2">{product.brand}</p>
        )}
        {product.description && (
          <p className="text-sm text-qds-text-secondary mb-3 line-clamp-2">{product.description}</p>
        )}

        <div className="flex items-center justify-between">
          <div>
            {minPrice < Infinity && (
              <span className="text-sm font-medium text-qds-text-primary">
                From {formatCents(minPrice)}
              </span>
            )}
          </div>
          <div className="flex gap-2 text-xs text-qds-text-tertiary">
            {colorOptions.size > 0 && (
              <span>{colorOptions.size} color{colorOptions.size !== 1 ? "s" : ""}</span>
            )}
            {sizeOptions.size > 0 && (
              <span>{sizeOptions.size} size{sizeOptions.size !== 1 ? "s" : ""}</span>
            )}
          </div>
        </div>

        <Link
          to="/business/quote"
          className="mt-4 w-full inline-flex items-center justify-center bg-qds-surface border border-qds-border px-4 py-2.5 rounded-lg text-sm font-medium text-qds-text-primary hover:bg-qds-surface-hover transition-colors min-h-[44px]"
        >
          Add to quote
        </Link>
      </div>
    </article>
  );
}
